import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Paper,
  FormControlLabel,
  Checkbox,
  Divider,
  Typography,
  FormControl,
  Tooltip,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";
import { UpdateInvoice } from "../../services/InvoiceService";
import CommonLoader from "../common/CommonLoader";
import InvoiceStatusSelect from "../common/InvoiceStatusSelect";

// Fields VLP's InvoiceManager shows read-only (grey background) stay read-only here too.
const readOnlyInputProps = { readOnly: true, sx: { backgroundColor: "action.hover" } };

const EditInvoice = ({ invoiceData }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    invoiceNumber: "",
    orderNumber: "",
    glCode: "",
    poNumber: "",
    billingCode: "",
    salesPerson: "",
    billingName: "",
    billingContact: "",
    billingAddress: "",
    billingAddress2: "",
    billingCity: "",
    billingState: "",
    billingZip: "",
    billingPhone: "",
    billingFax: "",
    billingEmail: "",
    invoiceDueDate: "",
    dateInvoiced: "",
    quoteNumber: "",
    quotedAmount: "",
    listPrice: "",
    invoiceAmount: "",
    miscellaneousCharges: "",
    adjustableTotal: "",
    nonadjustableTotal: "",
    discountPercent: "",
    surchargePercent: "",
    salesTaxPercent: "",
    applyLineItemDiscount: false,
    applyDiscToMisc: false,
    deposit: "",
    exportDate: "",
    arGlCode: "",
    invcomments: "",
    discountAmount: "",
    surchargeAmount: "",
    recordid: "",
    dateCreated: "",
    shippingCharge: "",
    creditAmount: "",
    paymentNumber: "",
    paymentType: "",
    paymentDate: "",
    createdBy: "",
    invoiceSent: false,
    dateInvoiceSent: "",
    dateInvoiceResent: "",
    qbInvoiceAmount: "",
    accountNumber: "",
    qbCustomFieldSalesRep: "",
    qbDescription: "",
    syncToQbo: false,
    dateGenerated: "",
    dateReviewed: "",
    dateReviewed2: "",
    generatedBy: "",
    reviewedBy: "",
    reviewedBy2: "",
    invoiceStatus: "",
    dateSent: "",
    correctionComments: "",
    needsCorrectionDate: "",
    paymentPending: "",
    stripeId: "",

    clientBillingName: "",
    turnAroundTime: "",
    processId: "",
    projectCategory: "",
    orderStatus: "",
    reportContact: "",
    billingComments: "",
    qbAccountNumber: "",
    operator: "",

    eowBit: true,
    emailBit: false,
    printBit: false,
    referToChain: false,
    eomBit: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    // Extract the date part from the ISO string (yyyy-MM-dd)
    return dateString.split("T")[0];
  };
  useEffect(() => {
    if (invoiceData) {
      setFormData(invoiceData);
    }
  }, [invoiceData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Same as VLP's tbDate_invoiced_TextChanged: due date follows date invoiced + 30 days.
  const handleDateInvoicedChange = (e) => {
    const { value } = e.target;
    let invoiceDueDate = formData.invoiceDueDate;
    if (value) {
      const due = new Date(`${value}T00:00:00Z`);
      due.setUTCDate(due.getUTCDate() + 30);
      invoiceDueDate = due.toISOString().split("T")[0];
    }
    setFormData({ ...formData, dateInvoiced: value, invoiceDueDate });
  };

  const validateForm = () => {
    const errors = {};
    // Example validation for required fields
    if (!formData.invoiceNumber)
      errors.invoiceNumber = "Invoice Number is required.";
    // Add more validation rules as necessary

    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((error) =>
        NotificationManager.error(error)
      );
      return false;
    }
    return true;
  };

  const handleSaveClick = async () => {
    if (validateForm()) {
      const parsedData = parseInvoiceData(formData);
      try {
        setLoading(true);
        await UpdateInvoice(parsedData);
        NotificationManager.success("Invoice updated successfully.");
        navigate("/vlp/invoices");
      } catch (error) {
        NotificationManager.error("Failed to update invoice.");
      } finally {
        setLoading(false);
      }
    }
  };
  const formatDateTime = (date) => {
    if (!date) return ""; // Return an empty string if there's no date
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(date).toLocaleString("en-US", options);
  };

  const parseInvoiceData = (data) => {
    return {
      invoiceNumber: data.invoiceNumber,
      orderNumber: parseInt(data.orderNumber, 10) || null,
      glCode: data.glCode,
      poNumber: data.poNumber,
      billingCode: data.billingCode,
      salesPerson: data.salesPerson,
      billingName: data.billingName,
      billingContact: data.billingContact,
      billingAddress: data.billingAddress,
      billingAddress2: data.billingAddress2,
      billingCity: data.billingCity,
      billingState: data.billingState,
      billingZip: data.billingZip,
      billingPhone: data.billingPhone,
      billingFax: data.billingFax,
      billingEmail: data.billingEmail,
      // Editable dates go as plain yyyy-MM-dd so no timezone shift moves the day.
      invoiceDueDate: formatDateForInput(data.invoiceDueDate) || null,
      dateInvoiced: formatDateForInput(data.dateInvoiced) || null,
      quoteNumber: data.quoteNumber,
      quotedAmount: parseFloat(data.quotedAmount) || 0,
      listPrice: parseFloat(data.listPrice) || 0,
      invoiceAmount: parseFloat(data.invoiceAmount) || 0,
      miscellaneousCharges: parseFloat(data.miscellaneousCharges) || 0,
      adjustableTotal: parseFloat(data.adjustableTotal) || 0,
      nonadjustableTotal: parseFloat(data.nonadjustableTotal) || 0,
      discountPercent: parseFloat(data.discountPercent) || 0,
      surchargePercent: parseFloat(data.surchargePercent) || 0,
      salesTaxPercent: parseFloat(data.salesTaxPercent) || 0,
      applyLineItemDiscount: Boolean(data.applyLineItemDiscount),
      applyDiscToMisc: Boolean(data.applyDiscToMisc),
      deposit: parseFloat(data.deposit) || 0,
      exportDate: data.exportDate ? new Date(data.exportDate) : null,
      arGlCode: data.arGlCode,
      invcomments: data.invcomments,
      discountAmount: parseFloat(data.discountAmount) || 0,
      surchargeAmount: parseFloat(data.surchargeAmount) || 0,
      recordid: parseInt(data.recordid, 10) || 0,
      dateCreated: data.dateCreated ? new Date(data.dateCreated) : null,
      shippingCharge: parseFloat(data.shippingCharge) || 0,
      creditAmount: parseFloat(data.creditAmount) || 0,
      paymentNumber: data.paymentNumber,
      paymentType: data.paymentType,
      paymentDate: data.paymentDate ? new Date(data.paymentDate) : null,
      createdBy: data.createdBy,
      invoiceSent: Boolean(data.invoiceSent),
      dateInvoiceSent: data.dateInvoiceSent
        ? new Date(data.dateInvoiceSent)
        : null,
      dateInvoiceResent: data.dateInvoiceResent
        ? new Date(data.dateInvoiceResent)
        : null,
      qbInvoiceAmount: parseFloat(data.qbInvoiceAmount) || 0,
      accountNumber: data.accountNumber,
      qbCustomFieldSalesRep: data.qbCustomFieldSalesRep,
      qbDescription: data.qbDescription,
      syncToQbo: Boolean(data.syncToQbo),
      dateGenerated: data.dateGenerated ? new Date(data.dateGenerated) : null,
      dateReviewed: data.dateReviewed ? new Date(data.dateReviewed) : null,
      dateReviewed2: data.dateReviewed2 ? new Date(data.dateReviewed2) : null,
      generatedBy: data.generatedBy,
      reviewedBy: data.reviewedBy,
      reviewedBy2: data.reviewedBy2,
      invoiceStatus: data.invoiceStatus,
      dateSent: data.dateSent ? new Date(data.dateSent) : null,
      correctionComments: data.correctionComments,
      needsCorrectionDate: data.needsCorrectionDate
        ? new Date(data.needsCorrectionDate)
        : null,
      paymentPending: data.paymentPending,
      stripeId: data.stripeId,

      // Client-related fields
      clientBillingName: data.clientBillingName,
      turnAroundTime: data.turnAroundTime,
      processId: data.processId,
      projectCategory: data.projectCategory,
      orderStatus: data.orderStatus,
      reportContact: data.reportContact,
      billingComments: data.billingComments,
      qbAccountNumber: data.qbAccountNumber,
      operator: data.operator,

      // Boolean fields
      eowBit: Boolean(data.eowBit),
      emailBit: Boolean(data.emailBit),
      printBit: Boolean(data.printBit),
      referToChain: Boolean(data.referToChain),
      eomBit: Boolean(data.eomBit),
    };
  };

  const handleCancelClick = () => {
    navigate("/vlp/invoices");
  };

  return (
    <>
      {loading && <CommonLoader loading={loading} />}
      {/* Same section layout as Edit Lab Order (EditOrderInfo): collapsible
          sections, 15-column grid from md up so md={3} items land 5 per row */}
      <Paper elevation={3} sx={{ padding: 3, marginTop: 1 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Billing Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
        <Grid container spacing={2} columns={{ xs: 12, md: 15 }}>

          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="PO Number"
              name="poNumber"
              value={formData.poNumber}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

          
         
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing Name"
              name="billingName"
              value={formData.billingName}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing Contact"
              name="billingContact"
              value={formData.billingContact}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing Address"
              name="billingAddress"
              value={formData.billingAddress}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing Address 2"
              name="billingAddress2"
              value={formData.billingAddress2}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing City"
              name="billingCity"
              value={formData.billingCity}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing State"
              name="billingState"
              value={formData.billingState}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing Zip"
              name="billingZip"
              value={formData.billingZip}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing Phone"
              name="billingPhone"
              value={formData.billingPhone}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing Fax"
              name="billingFax"
              value={formData.billingFax}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing Email"
              name="billingEmail"
              value={formData.billingEmail}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

        </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Price Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
        <Grid container spacing={2} columns={{ xs: 12, md: 15 }}>

          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="List Price"
              name="listPrice"
              InputProps={readOnlyInputProps}
              type="number"
              value={formData.listPrice}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Invoice Amount"
              name="invoiceAmount"
              InputProps={readOnlyInputProps}
              type="number"
              value={formData.invoiceAmount}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Miscellaneous Charges"
              name="miscellaneousCharges"
              type="number"
              value={formData.miscellaneousCharges}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Adjustable Total"
              name="adjustableTotal"
              type="number"
              value={formData.adjustableTotal}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Non-adjustable Total"
              name="nonadjustableTotal"
              type="number"
              value={formData.nonadjustableTotal}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          {/* Fifth row */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Discount Percent"
              name="discountPercent"
              type="number"
              value={formData.discountPercent}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
         

          {/* Sixth row */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Deposit"
              name="deposit"
              type="number"
              value={formData.deposit}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Invoice Comments"
              name="invcomments"
              value={formData.invcomments}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Discount Amount"
              name="discountAmount"
              type="number"
              value={formData.discountAmount}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
         
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Shipping Charge"
              name="shippingCharge"
              type="number"
              value={formData.shippingCharge}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Credit Amount"
              name="creditAmount"
              type="number"
              value={formData.creditAmount}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Payment Number"
              name="paymentNumber"
              value={formData.paymentNumber}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Payment Type"
              name="paymentType"
              value={formData.paymentType}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

         
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Account Number"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="QB Account Number"
              name="qbAccountNumber"
              value={formData.qbAccountNumber || ""}
              fullWidth
              size="small"
              disabled
            />
          </Grid>
         

        </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Generic Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
        <Grid container spacing={2} columns={{ xs: 12, md: 15 }}>

          <Grid item xs={12} sm={6} md={3}>
            <Tooltip
              title={
                formatDateTime(formData.dateCreated) || "No date available"
              }
            >
              <TextField
                label="Created By"
                name="createdBy"
                InputProps={readOnlyInputProps}
                value={formData.createdBy}
                fullWidth
                size="small"
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip
              title={
                formatDateTime(formData.dateGenerated) || "No date available"
              }
            >
              <TextField
                label="Generated By"
                name="generatedBy"
                InputProps={readOnlyInputProps}
                value={formData.generatedBy}
                fullWidth
                size="small"
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Tooltip
              title={
                formatDateTime(formData.dateReviewed) || "No date available"
              }
            >
              <TextField
                label="Reviewed By"
                name="reviewedBy"
                InputProps={readOnlyInputProps}
                value={formData.reviewedBy}
                fullWidth
                size="small"
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Tooltip
              title={
                formatDateTime(formData.dateReviewed2) || "No date available"
              }
            >
              <TextField
                label="Reviewed By 2"
                name="reviewedBy2"
                InputProps={readOnlyInputProps}
                value={formData.reviewedBy2}
                fullWidth
                size="small"
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InvoiceStatusSelect
                selectedStatus={formData.invoiceStatus}
                onStatusChange={(newStatus) => {
                  // Handle status change logic here
                  setFormData({ ...formData, invoiceStatus: newStatus });
                }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Correction Comments"
              name="correctionComments"
              value={formData.correctionComments}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Stripe ID"
              name="stripeId"
              value={formData.stripeId}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Dates here */}

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Invoice Due Date"
              name="invoiceDueDate"
              type="date"
              value={formatDateForInput(formData.invoiceDueDate)}
              onChange={handleChange}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date Invoiced"
              name="dateInvoiced"
              type="date"
              value={formatDateForInput(formData.dateInvoiced)}
              onChange={handleDateInvoicedChange}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date Sent"
              name="dateSent"
              type="date"
              value={formatDateForInput(formData.dateSent)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date Generated"
              name="dateGenerated"
              InputProps={readOnlyInputProps}
              type="date"
              value={formatDateForInput(formData.dateGenerated)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date Reviewed"
              name="dateReviewed"
              InputProps={readOnlyInputProps}
              type="date"
              value={formatDateForInput(formData.dateReviewed)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date Reviewed 2"
              name="dateReviewed2"
              InputProps={readOnlyInputProps}
              type="date"
              value={formatDateForInput(formData.dateReviewed2)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date Invoice Sent"
              name="dateInvoiceSent"
              InputProps={readOnlyInputProps}
              type="date"
              value={formatDateForInput(formData.dateInvoiceSent)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date Invoice Resent"
              name="dateInvoiceResent"
              InputProps={readOnlyInputProps}
              type="date"
              value={formatDateForInput(formData.dateInvoiceResent)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Payment Date"
              name="paymentDate"
              InputProps={readOnlyInputProps}
              type="date"
              value={formatDateForInput(formData.paymentDate)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date Created"
              name="dateCreated"
              InputProps={readOnlyInputProps}
              type="date"
              value={formatDateForInput(formData.dateCreated)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Export Date"
              name="exportDate"
              type="date"
              value={formatDateForInput(formData.exportDate)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* New controls here */}

         
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Turn Around Time "
              name="turnAroundTime "
              InputProps={readOnlyInputProps}
              value={formData.turnAroundTime}
              fullWidth
              size="small"
            />
          </Grid>

          

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Project Category "
              name="projectCategory "
              InputProps={readOnlyInputProps}
              value={formData.projectCategory}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Order Status "
              name="orderStatus "
              value={formData.orderStatus}
              fullWidth
              size="small"
            />
          </Grid>
         

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Billing Comments "
              name="billingComments "
              InputProps={readOnlyInputProps}
              value={formData.billingComments}
              fullWidth
              size="small"
            />
          </Grid>

          {/* Check Boxes */}

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.eowBit}
                  name="eowBit"
                  disabled
                  label="EOW"
                />
              }
              label="EOW"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.emailBit}
                  name="emailBit"
                  disabled
                  label="Email"
                />
              }
              label="Email"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.printBit}
                  name="printBit"
                  disabled
                  label="Print"
                />
              }
              label="Print"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.referToChain}
                  name="referToChain"
                  disabled
                  label="Refer To Chain"
                />
              }
              label="Refer To Chain"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.syncToQbo}
                  name="syncToQbo"
                  label="Sync To Qbo"
                  onChange={handleChange}
                />
              }
              label="Sync To Qbo"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.eomBit}
                  name="eomBit"
                  disabled
                  label="eomBit"
                />
              }
              label="EOM"
            />
          </Grid>

        </Grid>
          </AccordionDetails>
        </Accordion>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveClick}
          >
            Save
          </Button>
        </Box>
      </Paper>
    </>
  );
};

export default EditInvoice;
